import { API_CONFIG } from '../constants';
import { getAccessToken } from './auth';

export const apiRequest = async (endpoint, options = {}) => {
  const url = `${process.env.REACT_APP_BASE_URL?.replace(/=+$/, '')}${endpoint}`;

  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  const config = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);

    if (!response.ok) {
      // Handle specific error cases
      if (response.status === 404) {
        console.warn(`API endpoint not found: ${url}`);
        throw new Error(`Endpoint not found: ${endpoint}`);
      } else if (response.status === 401) {
        console.warn(`Unauthorized access to: ${url}`);
        throw new Error(`Unauthorized access to: ${endpoint}`);
      } else if (response.status >= 500) {
        console.warn(`Server error for: ${url}`);
        throw new Error(`Server error: ${response.status}`);
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    }

    return await response.json();
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};

export const podApiRequest = async (endpoint, options = {}) => {
  const url = `${process.env.REACT_APP_BASE_URL_POD?.replace(/=+$/, '')}${endpoint}`;

  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  const config = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);

    if (!response.ok) {
      // Handle specific error cases
      if (response.status === 404) {
        console.warn(`Pod API endpoint not found: ${url}`);
        throw new Error(`Pod endpoint not found: ${endpoint}`);
      } else if (response.status === 401) {
        console.warn(`Unauthorized access to pod: ${url}`);
        throw new Error(`Unauthorized access to pod: ${endpoint}`);
      } else if (response.status >= 500) {
        console.warn(`Pod server error for: ${url}`);
        throw new Error(`Pod server error: ${response.status}`);
      } else {
        throw new Error(`Pod HTTP error! status: ${response.status}`);
      }
    }

    return await response.json();
  } catch (error) {
    console.error('Pod API request failed:', error);
    throw error;
  }
};


/**
 * Fetch current patient data from API using pod IP
 * @param {string} podIP - Pod IP address
 * @returns {Promise<Object>} - Patient data
 */
export const fetchCurrentPatient = async (podIP) => {
  try {
    const response = await podApiRequest(API_CONFIG.ENDPOINTS.CURRENT_PATIENT, {
      method: 'POST',
      body: JSON.stringify({ pod_ip: podIP }),
    });

    // Transform the response to match expected format
    if (response && response.patient) {
      return {
        id: response.patient.patient_id.toString(),
        name: response.patient.patient_name,
        age: response.patient.patient_age,
        gender: response.patient.patient_gender,
        email: response.patient.patient_email,
        patientId: response.patient.patient_id.toString(),
        sessionId: response.patient.session_id.toString(),
        requestId: response.patient.request_id.toString(),
        podIP: response.pod
      };
    }

    throw new Error('Invalid response format from current_patient API');
  } catch (error) {
    console.error('Error fetching current patient:', error);
    // Fallback to mock data if API fails
    console.warn('Using mock data due to API error:', error);
    return {
      id: 'mock-user-123',
      name: 'John',
      age: 30,
      gender: 'Male',
      email: 'john@example.com',
      isMockData: true,
      patientId: '54',
      sessionId: '123',
      requestId: '123',
      podIP: podIP
    };
  }
};


/**
 * Submit vitals data to API
 * @param {Object} vitalsData - Vitals measurement data
 * @returns {Promise<Object>} - API response
 */
export const submitVitalsData = async (vitalsData) => {
  return await apiRequest(API_CONFIG.ENDPOINTS.VITALS, {
    method: 'POST',
    body: JSON.stringify(vitalsData),
  });
};



/**
 * Fetch questionnaire data from API
 * @param {string} type - Assessment type (WELLNESS, LIFESTYLE, HEALTH_ASSESSMENT)
 * @returns {Promise<Array>} - Questionnaire data
 */
export const fetchQuestionnaire = async (type) => {
  try {
    const authToken = await getAccessToken();

    return await apiRequest(`${API_CONFIG.ENDPOINTS.QUESTIONNAIRE}?type=${type}`, {
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Accept-Language': localStorage.getItem('app-language') || 'en',
      },
    });
  } catch (error) {
    console.error('Error fetching questionnaire:', error);
    throw error;
  }
};

/**
 * Fetch pod data from API by IP address
 * @param {string} ipAddress - IP address to fetch pod data for
 * @returns {Promise<Object>} - Pod data
 */
export const fetchPodData = async (ipAddress) => {
  try {
    const authToken = await getAccessToken();
    const token = authToken;

    return await apiRequest(API_CONFIG.ENDPOINTS.POD_IP(ipAddress), {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  } catch (error) {
    console.error('Error fetching pod data:', error);
    throw error;
  }
};

export const submitQuestionnaire = async (submissionData) => {
  try {
    const authToken = await getAccessToken();

    return await apiRequest(`${API_CONFIG.ENDPOINTS.SUBMIT_QUESTIONNAIRE}?source=POD`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Accept': 'application/json',
        'Accept-Language': localStorage.getItem('app-language') || 'en',
      },
      body: JSON.stringify(submissionData),
    });
  } catch (error) {
    console.error('Error submitting questionnaire:', error);
    throw error;
  }
};

export const sendConsultationEmails = async (podIp, patientId) => {
  try {
    const authToken = await getAccessToken();

    return await apiRequest(API_CONFIG.ENDPOINTS.POD_CONSULTATION_EMAILS, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        podIp,
        patientId
      }),
    });
  } catch (error) {
    console.error('Error sending consultation emails:', error);
    throw error;
  }
};


export const endSession = async (podIP, sessionId) => {
  try {
    const authToken = await getAccessToken();

    return await apiRequest(`/mt-vmr/pod/${podIP}/admin-checkout`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        sessionId: sessionId
      }),
    });
  } catch (error) {
    console.error('Error ending session:', error);
    throw error;
  }
};
